// Console sign-in: Supabase Auth email/password + TOTP (authenticator app). Only used for /console.
import { createClient, type SupabaseClient } from '@supabase/supabase-js';

export interface ConsoleSession {
  accessToken: string;
  email: string;
  aal: 'aal1' | 'aal2';
}

export interface ConsoleAuth {
  getSession(): Promise<ConsoleSession | null>;
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  verifiedFactorId(): Promise<string | null>;
  enroll(): Promise<{ factorId: string; qrSvg: string; secret: string }>;
  verify(factorId: string, code: string): Promise<void>;
}

const THAI_AUTH_ERRORS: [RegExp, string][] = [
  [/invalid login credentials/i, 'อีเมลหรือรหัสผ่านไม่ถูกต้อง'],
  [/invalid (totp|code)|invalid mfa/i, 'รหัส 6 หลักไม่ถูกต้อง ลองใหม่'],
  [/email not confirmed/i, 'กรุณายืนยันอีเมลก่อน (ดูกล่องจดหมาย)'],
];

function friendly(e: { message?: string } | null): Error {
  const msg = e?.message ?? 'เข้าสู่ระบบไม่สำเร็จ';
  const hit = THAI_AUTH_ERRORS.find(([re]) => re.test(msg));
  return new Error(hit ? hit[1] : msg);
}

export function supabaseConsoleAuth(url: string, anonKey: string): ConsoleAuth {
  const sb: SupabaseClient = createClient(url, anonKey, { auth: { persistSession: true, autoRefreshToken: true } });
  return {
    async getSession() {
      const { data } = await sb.auth.getSession();
      const s = data.session;
      if (!s) return null;
      const { data: aal } = await sb.auth.mfa.getAuthenticatorAssuranceLevel();
      return { accessToken: s.access_token, email: s.user.email ?? '', aal: aal?.currentLevel === 'aal2' ? 'aal2' : 'aal1' };
    },
    async signIn(email, password) {
      const { error } = await sb.auth.signInWithPassword({ email, password });
      if (error) throw friendly(error);
    },
    async signOut() {
      await sb.auth.signOut();
    },
    async verifiedFactorId() {
      const { data, error } = await sb.auth.mfa.listFactors();
      if (error) throw friendly(error);
      return data.totp.find((f) => f.status === 'verified')?.id ?? null;
    },
    async enroll() {
      const { data, error } = await sb.auth.mfa.enroll({ factorType: 'totp', friendlyName: `SalonFlow ${Date.now()}` });
      if (error || !data) throw friendly(error);
      return { factorId: data.id, qrSvg: data.totp.qr_code, secret: data.totp.secret };
    },
    async verify(factorId, code) {
      const { error } = await sb.auth.mfa.challengeAndVerify({ factorId, code });
      if (error) throw friendly(error);
    },
  };
}
